<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';

import Divider from '@/components/Shared/Divider/Divider.vue';

const props = withDefaults(
  defineProps<{
    title?: string;
    addDivider?: boolean;
    subtitle?: string;
    nextSectionId?: string;
    isBlurred?: boolean;
    isContinueLogicEnabled?: boolean;
    shouldStopNavigation?: boolean;
  }>(),
  {
    addDivider: true,
    nextSectionId: undefined,
    isBlurred: false,
    isContinueLogicEnabled: false,
    shouldStopNavigation: false,
  },
);

const emits = defineEmits<{
  (e: 'click:continue'): void;
}>();

const hasClickedContinue = ref(false);

const shouldNavigate = computed(() => {
  return props.nextSectionId && !props.shouldStopNavigation;
});

const displayingTitle = computed(() => {
  return props.title;
});

const displayingSubtitle = computed(() => {
  return props.subtitle;
});

const isDisplayingContinueButton = computed(() => {
  return props.isContinueLogicEnabled && !hasClickedContinue.value;
});

function navigateToNextSectionId() {
  emits('click:continue');
  hasClickedContinue.value = true;
  if (shouldNavigate.value) {
    const hashedTag = `#${props.nextSectionId}`;
    nextTick(() => {
      const element = document.querySelector(hashedTag);

      if (element) {
        const offset = 64;
        const topOffset = element.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: topOffset, behavior: 'smooth' });
      }
    });
  }
}
</script>

<template>
  <div :class="{ 'opacity-20': isBlurred }">
    <h3
      v-if="title"
      class="font-sans text-2xl font-bold leading-9 text-black"
      :class="{
        'mb-1': subtitle,
        'mb-4': !subtitle,
      }"
    >
      {{ displayingTitle }}
    </h3>
    <p v-if="subtitle" class="mb-6 font-sans text-base font-normal text-shade-800">
      {{ displayingSubtitle }}
    </p>
    <slot />

    <div v-if="nextSectionId" class="flex justify-end pt-[22px]">
      <button
        v-if="isDisplayingContinueButton"
        class="h-10 w-[96px] rounded-md bg-shade-900 px-4 py-2.5 text-sm font-semibold text-tint-0"
        :disabled="isBlurred"
        @click="navigateToNextSectionId"
      >
        Continue
      </button>
    </div>

    <Divider class="my-10" v-if="addDivider" />
  </div>
</template>
